/**
 * i18nController.js
 * Client-side internationalization (i18n) controller
 *
 * Subscribes to route navigation events and applies microcopy to DOM elements
 * with data-meta-copywrite-i118n attributes after DOM/Shadow DOM hydration.
 *
 * Usage:
 *   import { I18nController } from './controllers/i18nController.js';
 *
 *   // Initialize on app startup
 *   I18nController.initialize();
 *
 *   // Trigger manual refresh (e.g., after language change)
 *   I18nController.refresh();
 *
 *   // Change language
 *   I18nController.setLanguage('es');
 *
 * HTML Usage:
 *   <h1 data-meta-copywrite-i118n='{"token": "welcome_back", "attribute": "textContent"}'>Loading...</h1>
 *   <input data-meta-copywrite-i118n='{"token": "enter_your_email", "attribute": "placeholder"}' />
 *   <button data-meta-copywrite-i118n='{"token": "sign_in", "attribute": "textContent"}'>...</button>
 */

import { MicrocopyManager } from '../../../../shared/meta_microcopy_i18n.js';
import { FEATURE_FLAGS } from '../../../../shared/featureFlags.js';

export class I18nController {
  static #initialized = false;
  static #currentRoute = null;
  static #observer = null;

  /**
   * Initialize the i18n controller
   * Sets up MicrocopyManager context and subscribes to route changes
   */
  static initialize() {
    if (this.#initialized) {
      console.warn('[I18nController] Already initialized');
      return;
    }

    // Load context from featureFlags and localStorage
    const context = this.#loadContext();

    // Set MicrocopyManager context
    MicrocopyManager.setContext(context);

    // Always log context on initialization for debugging
    console.log('[I18nController] Initialized with context:', context);
    console.log('[I18nController] Available languages:', this.getAvailableLanguages());

    if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
      console.log('[I18nController] Debug mode enabled');
    }

    // Subscribe to Neodigm Carousel navigation events
    this.#subscribeToRouteChanges();

    // Set up MutationObserver for dynamic DOM changes
    this.#setupMutationObserver();

    this.#initialized = true;

    // Apply i18n to current page on initial load
    this.refresh();
  }

  /**
   * Map Vite's MODE to standardized build modes
   * Standardized: LOCAL, DEV, SIT, QA, PROD
   * @param {string} viteMode - Vite's import.meta.env.MODE
   * @returns {string} Standardized build mode
   */
  static #mapViteModeToStandardBuildMode(viteMode) {
    const modeMap = {
      'development': 'LOCAL',
      'local': 'LOCAL',
      'dev': 'DEV',
      'sit': 'SIT',
      'qa': 'QA',
      'uat': 'QA',  // Map UAT to QA
      'production': 'PROD',
      'prod': 'PROD'
    };

    const normalized = viteMode.toLowerCase();
    return modeMap[normalized] || 'DEV';
  }

  /**
   * Load context from featureFlags and localStorage
   * @returns {Object} Context object
   */
  static #loadContext() {
    // Get build mode from environment or featureFlags
    // Standardized build modes: LOCAL, DEV, SIT, QA, PROD
    // Vite mode mapping: development->LOCAL, production->PROD, custom modes map directly
    const envMode = import.meta.env.MODE || 'development';
    const buildMode = this.#mapViteModeToStandardBuildMode(envMode);

    // Get whitelabel from localStorage or featureFlags
    const whitelabel = localStorage.getItem(FEATURE_FLAGS.FF_SSE_LS_WHITELABEL) ||
                       FEATURE_FLAGS.FF_SSE_I18N_WHITELABEL;

    // Get language from localStorage or featureFlags
    const lang = localStorage.getItem(FEATURE_FLAGS.FF_SSE_LS_USER_LANG) ||
                 FEATURE_FLAGS.FF_SSE_I18N_LANG;

    return {
      buildMode,
      product: FEATURE_FLAGS.FF_SSE_I18N_PRODUCT,
      whitelabel,
      lang
    };
  }

  /**
   * Subscribe to Neodigm Carousel route changes
   */
  static #subscribeToRouteChanges() {
    // Hook into neodigmCarousel's setOnAfterNav callback
    if (typeof neodigmCarousel !== 'undefined' && neodigmCarousel.setOnAfterNav) {
      neodigmCarousel.setOnAfterNav(
        (carouselId, pageIndex, pageName) => {
          if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
            console.log('[I18nController] Route changed:', pageName);
          }

          this.#currentRoute = pageName;

          // Apply i18n with delay to ensure DOM hydration
          setTimeout(() => {
            this.refresh();
          }, FEATURE_FLAGS.FF_SSE_I18N_APPLY_DELAY_MS);
        },
        FEATURE_FLAGS.FF_CLIENT_CAROUSEL_ID
      );
    } else {
      console.warn('[I18nController] neodigmCarousel not found. Using fallback polling.');
      this.#fallbackPolling();
    }
  }

  /**
   * Fallback polling mechanism if neodigmCarousel is not available
   */
  static #fallbackPolling() {
    // Poll for route changes every 500ms (basic fallback)
    setInterval(() => {
      const currentPage = document.querySelector('[data-n55-carousel-page-name]');
      const pageName = currentPage?.getAttribute('data-n55-carousel-page-name');

      if (pageName && pageName !== this.#currentRoute) {
        this.#currentRoute = pageName;
        this.refresh();
      }
    }, 500);
  }

  /**
   * Set up MutationObserver to watch for dynamic DOM changes
   * Useful for SPAs with component-based rendering
   */
  static #setupMutationObserver() {
    if (this.#observer) return;

    this.#observer = new MutationObserver((mutations) => {
      let shouldRefresh = false;

      for (const mutation of mutations) {
        // Check if any added nodes have i18n attributes
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute('data-meta-copywrite-i118n') ||
                node.querySelector('[data-meta-copywrite-i118n]')) {
              shouldRefresh = true;
              break;
            }
          }
        }
        if (shouldRefresh) break;
      }

      if (shouldRefresh) {
        if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
          console.log('[I18nController] DOM mutation detected, refreshing i18n');
        }
        this.refresh();
      }
    });

    this.#observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Apply microcopy to all elements with data-meta-copywrite-i118n attribute
   * Searches both regular DOM and Shadow DOM
   */
  static refresh() {
    if (!this.#initialized) {
      console.warn('[I18nController] Not initialized. Call initialize() first.');
      return;
    }

    // Process regular DOM
    this.#processElements(document.body);

    // Process Shadow DOM (if any web components exist)
    this.#processShadowDOMs(document.body);

    if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
      console.log('[I18nController] Refresh complete');
    }
  }

  /**
   * Process elements with data-meta-copywrite-i118n attribute
   * @param {HTMLElement} rootElement - Root element to search within
   */
  static #processElements(rootElement) {
    const elements = rootElement.querySelectorAll('[data-meta-copywrite-i118n]');

    if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG && elements.length > 0) {
      console.log(`[I18nController] Processing ${elements.length} elements`);
    }

    elements.forEach((element) => {
      try {
        const configStr = element.getAttribute('data-meta-copywrite-i118n');
        const config = JSON.parse(configStr);

        if (!config.token) {
          console.warn('[I18nController] Missing token in config:', config);
          return;
        }

        // Default attribute is textContent
        const attribute = config.attribute || 'textContent';

        // Get microcopy from MicrocopyManager
        const microcopy = MicrocopyManager.get(config.token);

        if (microcopy === null) {
          console.warn(`[I18nController] Microcopy not found for token: ${config.token}`);
          return;
        }

        // Apply microcopy to element
        if (attribute === 'textContent') {
          element.textContent = microcopy;
        } else if (attribute === 'innerHTML') {
          element.innerHTML = microcopy;
        } else {
          // Standard HTML attributes (placeholder, title, aria-label, etc.)
          element.setAttribute(attribute, microcopy);
        }

        if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
          console.log(`[I18nController] Applied "${microcopy}" to ${attribute} for token: ${config.token}`);
        }
      } catch (err) {
        console.error('[I18nController] Error processing element:', err, element);
      }
    });
  }

  /**
   * Recursively process Shadow DOMs
   * @param {HTMLElement} rootElement - Root element to search within
   */
  static #processShadowDOMs(rootElement) {
    const shadowHosts = rootElement.querySelectorAll('*');

    shadowHosts.forEach((host) => {
      if (host.shadowRoot) {
        // Process elements within shadow DOM
        this.#processElements(host.shadowRoot);

        // Recursively process nested shadow DOMs
        this.#processShadowDOMs(host.shadowRoot);
      }
    });
  }

  /**
   * Change language and refresh all microcopy
   * @param {string} langCode - Language code (e.g., 'en', 'es', 'zh-Hans')
   */
  static setLanguage(langCode) {
    // Validate language is available
    const availableLanguages = MicrocopyManager.getAvailableLanguages();

    if (!availableLanguages.includes(langCode)) {
      console.error(`[I18nController] Language not available: ${langCode}. Available:`, availableLanguages);
      return;
    }

    // Save to localStorage
    localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_USER_LANG, langCode);

    // Update MicrocopyManager context
    MicrocopyManager.setContext({ lang: langCode });

    console.log('[I18nController] Language changed to:', langCode);

    // Fire custom event to notify all web components
    const event = new CustomEvent('language-changed', {
      bubbles: true,
      composed: true,
      detail: { lang: langCode }
    });
    document.dispatchEvent(event);

    // Refresh all microcopy
    this.refresh();
  }

  /**
   * Change whitelabel and refresh all microcopy
   * @param {string} whitelabel - Whitelabel identifier
   */
  static setWhitelabel(whitelabel) {
    // Save to localStorage
    localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_WHITELABEL, whitelabel);

    // Update MicrocopyManager context
    MicrocopyManager.setContext({ whitelabel });

    if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
      console.log('[I18nController] Whitelabel changed to:', whitelabel);
    }

    // Refresh all microcopy
    this.refresh();
  }

  /**
   * Get current language
   * @returns {string} Current language code
   */
  static getLanguage() {
    return MicrocopyManager.getContext().lang;
  }

  /**
   * Get available languages
   * @returns {string[]} Array of language codes
   */
  static getAvailableLanguages() {
    return MicrocopyManager.getAvailableLanguages();
  }

  /**
   * Get current context
   * @returns {Object} Current context
   */
  static getContext() {
    return MicrocopyManager.getContext();
  }

  /**
   * Clean up (remove observers, etc.)
   */
  static destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    this.#initialized = false;
    this.#currentRoute = null;

    if (FEATURE_FLAGS.FF_SSE_I18N_DEBUG) {
      console.log('[I18nController] Destroyed');
    }
  }
}

// Auto-initialize if in browser context
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      I18nController.initialize();
    });
  } else {
    // DOM already loaded
    I18nController.initialize();
  }
}
